import React from "react"
import { usePlatforms } from "../../contexts/PlatformsContext"

// Shared row card for the "Top Revenued / Top Downloads / Top Average Rating /
// Top App Score / Top Reviewed" ranking pages — they all rendered this exact
// markup inline before, one copy per file. Each page still owns its own
// second metric (downloads / rating / app score / revenue), passed in as
// `secondMetricLabel` + `secondMetricValue` since the lock/upgrade-modal
// handling differs slightly per metric.
const formatPrice = (price) => {
  if (!price || price.amount === 0) return "Free"
  return `$${price.amount}`
}

const renderCategories = (categoryString) => {
  if (!categoryString) return null
  const categories = categoryString.includes("•")
    ? categoryString.split("•").map((cat) => cat.trim()).filter(Boolean)
    : [categoryString.trim()]
  return categories.map((category, idx) => (
    <span key={idx} className="app-category">{category}</span>
  ))
}

export default function AppRankCard({
  app,
  // Omit for pages that don't rank items (e.g. Top Reviewed).
  rank,
  // Falls back to whichever single platform the page's filter is scoped to
  // when the app itself doesn't carry a platform field.
  fallbackPlatformId,
  secondMetricLabel,
  secondMetricValue,
  onClick,
}) {
  const { getPlatformIcon, getPlatformDisplayName } = usePlatforms()
  const platformId = app?.platform || fallbackPlatformId
  const platformIcon = platformId ? getPlatformIcon(platformId) : null
  const platformName = platformId ? getPlatformDisplayName(platformId) : null

  return (
    <div className="app-card" onClick={onClick}>
      <div className="app-info">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {rank !== undefined && rank !== null && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 26,
                  height: 22,
                  padding: "0 6px",
                  borderRadius: 6,
                  background: "linear-gradient(90deg, rgba(255,154,184,0.15), rgba(246,37,90,0.15))",
                  color: "#F6255A",
                  fontSize: 12,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                #{rank}
              </span>
            )}
            <h2 className="app-name" style={{ margin: 0 }}>{app?.name || "Demo App"}</h2>
          </div>

          <p className="app-developer" style={{ margin: 0 }}>
            {app?.publisher || app?.developer || "Unknown Publisher"}
          </p>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div className="app-categories">
              {renderCategories(app?.category || "Action")}
            </div>
            {platformIcon && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "3px 10px 3px 5px",
                  borderRadius: 999,
                  background: "rgba(255, 255, 255, 0.05)",
                  color: "#A1ACB8",
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                <img
                  src={platformIcon}
                  alt=""
                  aria-hidden="true"
                  style={{ width: 14, height: 14, objectFit: "contain", borderRadius: 2 }}
                  onError={(e) => { e.target.parentElement.style.display = "none" }}
                />
                {platformName}
              </span>
            )}
          </div>
        </div>

        <div className="app-metrics">
          <div className="metric-group">
            <span className="metric-label">Price</span>
            <span className="metric-value">{formatPrice(app?.price) || "$39.99"}</span>
          </div>
          <div className="metric-group">
            <span className="metric-label">{secondMetricLabel}</span>
            {secondMetricValue}
          </div>
        </div>
      </div>

      <div className="app-image-container">
        {app?.image_url ? (
          <img src={app.image_url} alt={app.name} className="app-image" />
        ) : (
          <div className="app-image-placeholder">No Image</div>
        )}
      </div>
    </div>
  )
}
